import React, { useState, useEffect, useCallback, useMemo } from 'react';
import moment from 'moment';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import axiosInstance from '../../main/axios';
import SPButton from '../../common/form/spButton';
import TicketSuporteForm from '../../ticket_suporte/ticketSuporteForm';
import { openFromNotification } from '../../manutencao/ordem_servico/ordemServicoActions';
import { validaPermissao } from '../../auth/permissaoActions';
import { formatNotificationMessage } from '../services/notificationMessageUtils';
import {
  OS_STATUS_LABELS,
  OS_STATUS_BADGE,
  getErroId,
  getGrupoLabel,
  getEnderecoControlador,
  ordenarNotificacoes,
  buildTicketDataFromNotification,
  TICKET_STATUS_LABELS,
  TICKET_STATUS_BADGE,
} from './notificationListUtils';

function NotificationListPremium({
  notifications = [],
  onMarkAsRead,
  isMarkingAsRead = false,
  config,
  enableSelection = false,
  selectedKeys = [],
  onToggleSelect,
  onSelectAll,
  onNavigateAway,
  openFromNotification,
  history,
}) {
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [selectedNotificationForTicket, setSelectedNotificationForTicket] = useState(null);
  const [osPorErro, setOsPorErro] = useState({});
  const [ticketsPorErro, setTicketsPorErro] = useState({});
  const [carregandoOs, setCarregandoOs] = useState(false);
  const [carregandoTickets, setCarregandoTickets] = useState(false);
  const [abrindoOs, setAbrindoOs] = useState(false);

  const usaManutencao = config && config.modulo_manutencao;
  const usaTickets = config && config.modulo_tickets;
  const sortedNotifications = useMemo(() => ordenarNotificacoes(notifications), [notifications]);
  const todasSelecionadas =
    enableSelection && sortedNotifications.length > 0 && selectedKeys.length === sortedNotifications.length;

  const carregarOsPorErros = useCallback(
    () => {
      if (!usaManutencao) {
        setOsPorErro({});
        return;
      }

      const erroIds = sortedNotifications.map(getErroId).filter(Boolean);
      if (!erroIds.length) {
        setOsPorErro({});
        return;
      }

      setCarregandoOs(true);
      axiosInstance
        .get('/manutencao/ordens_servico/por_erros', { params: { ids: erroIds.join(',') } })
        .then(resp => setOsPorErro(resp.data || {}))
        .catch(() => setOsPorErro({}))
        .finally(() => setCarregandoOs(false));
    },
    [sortedNotifications, usaManutencao],
  );

  const carregarTicketsPorErros = useCallback(
    () => {
      if (!usaTickets) {
        setTicketsPorErro({});
        return;
      }

      const erroIds = sortedNotifications.map(getErroId).filter(Boolean);
      if (!erroIds.length) {
        setTicketsPorErro({});
        return;
      }

      setCarregandoTickets(true);
      axiosInstance
        .get('/ticket_suporte/por_erros', { params: { ids: erroIds.join(',') } })
        .then(resp => {
        const mapa = resp.data || {};
        const normalizado = {};
        Object.keys(mapa).forEach(chave => {
          const ticket = mapa[chave];
          normalizado[chave] = ticket;
          normalizado[String(chave)] = ticket;
          normalizado[Number(chave)] = ticket;
        });
        setTicketsPorErro(normalizado);
      })
        .catch(() => setTicketsPorErro({}))
        .finally(() => setCarregandoTickets(false));
    },
    [sortedNotifications, usaTickets],
  );

  useEffect(
    () => {
      carregarOsPorErros();
    },
    [carregarOsPorErros],
  );

  useEffect(
    () => {
      carregarTicketsPorErros();
    },
    [carregarTicketsPorErros],
  );

  const handleShowTicket = notification => {
    setSelectedNotificationForTicket(buildTicketDataFromNotification(notification));
    setShowTicketModal(true);
  };

  const handleOpenExistingTicket = (notification, ticketInfo) => {
    if (!ticketInfo || !ticketInfo.id) return;

    axiosInstance
      .get(`/ticket_suporte/${ticketInfo.id}`)
      .then(resp => {
        const ticket = resp.data || {};
        setSelectedNotificationForTicket({
          id: ticket.id,
          titulo: ticket.titulo || '',
          descricao: ticket.descricao || '',
          status: ticket.status || 'ABERTO',
          controlador_id: ticket.controlador_id || '',
          grupo_fisico_id: ticket.grupo_fisico_id || notification.grupo_fisico_id,
          numero_controlador: notification.numero_controlador,
          controlador_erro_id: ticket.controlador_erro_id || getErroId(notification),
          isFromError: false,
        });
        setShowTicketModal(true);
      })
      .catch(() => {
        setSelectedNotificationForTicket({
          id: ticketInfo.id,
          titulo: ticketInfo.titulo || `Ticket #${ticketInfo.id}`,
          descricao: '',
          status: ticketInfo.status || 'ABERTO',
          controlador_id: '',
          grupo_fisico_id: notification.grupo_fisico_id,
          numero_controlador: notification.numero_controlador,
          controlador_erro_id: getErroId(notification),
          isFromError: false,
        });
        setShowTicketModal(true);
      });
  };

  const handleShowOs = notification => {
    if (abrindoOs) return;

    const erroId = getErroId(notification);
    const osExistente = erroId ? osPorErro[erroId] : null;

    setAbrindoOs(true);
    openFromNotification(notification, osExistente)
      .then(() => {
        if (onNavigateAway) onNavigateAway();
        if (history) history.push('/manutencao-os');
      })
      .finally(() => setAbrindoOs(false));
  };

  const renderBadgeOs = notification => {
    if (!usaManutencao || !validaPermissao('CadOrdemServico')) return null;

    const erroId = getErroId(notification);
    if (!erroId) return null;

    const osInfo = osPorErro[erroId];

    if (carregandoOs && !osInfo) {
      return (
        <span className="notif-modal-os-badge notif-modal-os-badge--loading">
          <i className="fa fa-spinner fa-spin" /> Verificando OS...
        </span>
      );
    }

    if (!osInfo) {
      return (
        <button
          type="button"
          className="notif-modal-os-badge notif-modal-os-badge--none notif-modal-os-badge--btn"
          onClick={() => handleShowOs(notification)}
          disabled={abrindoOs}
          title="Abrir ordem de serviço"
        >
          <span className="notif-modal-os-badge-status">
            <i className="fa fa-clipboard-list" aria-hidden="true" />
            Sem OS vinculada
          </span>
          <span className="notif-modal-os-badge-action">
            Abrir OS ·
            <i className="fa fa-chevron-right notif-modal-os-badge-arrow" aria-hidden="true" />
          </span>
        </button>
      );
    }

    const badgeClass = OS_STATUS_BADGE[osInfo.status] || 'os-badge--gray';
    return (
      <button
        type="button"
        className={`notif-modal-os-badge notif-modal-os-badge--linked notif-modal-os-badge--btn ${badgeClass}`}
        onClick={() => handleShowOs(notification)}
        disabled={abrindoOs}
        title="Abrir ordem de serviço"
      >
        <span className="notif-modal-os-badge-label">
          <i className="fa fa-clipboard-list" aria-hidden="true" />
          {osInfo.numero} · {OS_STATUS_LABELS[osInfo.status] || osInfo.status}
        </span>
        <i className="fa fa-chevron-right notif-modal-os-badge-arrow" aria-hidden="true" />
      </button>
    );
  };

  const renderBadgeTicket = notification => {
    if (!usaTickets) return null;

    const erroId = getErroId(notification);
    if (!erroId) return null;

    const ticketInfo =
      ticketsPorErro[erroId] || ticketsPorErro[String(erroId)] || ticketsPorErro[Number(erroId)];

    if (carregandoTickets && !ticketInfo) {
      return (
        <span className="notif-modal-os-badge notif-modal-os-badge--loading">
          <i className="fa fa-spinner fa-spin" /> Verificando ticket...
        </span>
      );
    }

    if (!ticketInfo) {
      return (
        <button
          type="button"
          className="notif-modal-os-badge notif-modal-os-badge--none notif-modal-os-badge--btn"
          onClick={() => handleShowTicket(notification)}
          title="Abrir ticket de suporte"
        >
          <span className="notif-modal-os-badge-status">
            <i className="fa fa-ticket-alt" aria-hidden="true" />
            Sem ticket vinculado
          </span>
          <span className="notif-modal-os-badge-action">
            Abrir ticket ·
            <i className="fa fa-chevron-right notif-modal-os-badge-arrow" aria-hidden="true" />
          </span>
        </button>
      );
    }

    const badgeClass = TICKET_STATUS_BADGE[ticketInfo.status] || 'ticket-badge--gray';
    const statusLabel = TICKET_STATUS_LABELS[ticketInfo.status] || ticketInfo.status;
    return (
      <button
        type="button"
        className={`notif-modal-os-badge notif-modal-os-badge--linked notif-modal-os-badge--btn ${badgeClass}`}
        onClick={() => handleOpenExistingTicket(notification, ticketInfo)}
        title="Abrir ticket de suporte"
      >
        <span className="notif-modal-os-badge-label">
          <i className="fa fa-ticket-alt" aria-hidden="true" />
          #{ticketInfo.id} · {statusLabel}
        </span>
        <i className="fa fa-chevron-right notif-modal-os-badge-arrow" aria-hidden="true" />
      </button>
    );
  };

  if (sortedNotifications.length === 0) {
    return (
      <div className="notif-modal-empty">
        <i className="fa fa-bell-slash" />
        <span>Nenhuma notificação pendente</span>
      </div>
    );
  }

  return (
    <>
      <div className="notif-modal-table-wrap">
        <table className="notif-modal-table">
          <thead>
            <tr>
              {enableSelection && (
                <th className="notif-modal-th-check">
                  <input type="checkbox" onChange={onSelectAll} checked={todasSelecionadas} title="Selecionar todas" />
                </th>
              )}
              <th className="notif-modal-th-local">
                Grupo Físico <br /> Controlador
              </th>
              <th className="notif-modal-th-message">Mensagem</th>
              <th>Data/Hora</th>
              <th className="notif-modal-th-actions">Ações</th>
            </tr>
          </thead>
          <tbody>
            {sortedNotifications.map((notification, index) => {
              const rowKey = `${notification.controlador_id}-${notification.data_erro}-${index}`;
              const selecionada = selectedKeys.includes(rowKey);

              return (
                <tr key={rowKey} className={selecionada ? 'is-selected' : ''}>
                  {enableSelection && onToggleSelect && (
                    <td className="notif-modal-td-check" data-label="">
                      <input type="checkbox" checked={selecionada} onChange={() => onToggleSelect(rowKey)} />
                    </td>
                  )}
                  <td className="notif-modal-td-local" data-label="Grupo / Controlador">
                    <div className="notif-modal-local-grupo">{getGrupoLabel(notification)}</div>
                    <div className="notif-modal-local-ctrl" title={getEnderecoControlador(notification)}>
                      <span>Ctrl {notification.numero_controlador || notification.controlador_id || '—'}</span>
                      {getEnderecoControlador(notification) && (
                        <>
                          <span className="notif-modal-local-sep">·</span>
                          <span className="notif-modal-local-endereco">{getEnderecoControlador(notification)}</span>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="notif-modal-td-message" data-label="Mensagem">
                    <span className="notif-modal-message-text" title={notification.mensagem}>
                      {formatNotificationMessage(notification.mensagem)}
                    </span>
                    {(usaManutencao || usaTickets) && (
                      <div className="notif-modal-links-row">
                        {usaManutencao && renderBadgeOs(notification)}
                        {usaTickets && renderBadgeTicket(notification)}
                      </div>
                    )}
                  </td>
                  <td className="notif-modal-td-date" data-label="Data/Hora">
                    <span>{moment(notification.data_erro).format('DD/MM/YYYY')}</span>
                    <small>{moment(notification.hora_erro, 'HH:mm:ss').format('HH:mm')}</small>
                  </td>
                  <td className="notif-modal-td-actions" data-label="Ações">
                    <div className="notif-modal-actions-group">
                      <SPButton
                        type="button"
                        className="success btn-sm"
                        icon="check"
                        data-title="Marcar como lida"
                        onClick={() => onMarkAsRead(notification)}
                        disabled={isMarkingAsRead}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {usaTickets && showTicketModal && selectedNotificationForTicket && (
        <TicketSuporteForm
          key={selectedNotificationForTicket.id || 'new-ticket'}
          show={showTicketModal}
          onHide={() => {
            setShowTicketModal(false);
            setSelectedNotificationForTicket(null);
            carregarTicketsPorErros();
          }}
          ticket={selectedNotificationForTicket}
        />
      )}
    </>
  );
}

const mapStateToProps = state => ({
  config: state.auth.config,
});

const mapDispatchToProps = dispatch => bindActionCreators({ openFromNotification }, dispatch);

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(NotificationListPremium),
);
